import { Flex, Text } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/react'
import { t } from 'i18next'
import { Calendar, Check } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { DateTimeRangeModal } from '~/components/shared/DateTimeRangeModal'
import { DropdownActionButton } from '~/components/shared/Dropdown/DropdownActionButton'
import { MangoButtonVariant } from '~/components/shared/MangoButton'
import { TranslationKeys } from '~/locales/i18next'
import { DateRange } from '~/types/Shared'
import { hasValue } from '~/utils/common'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

import { DropdownMangoButton } from './Dropdown/DropdownMangoButton'
import { MenuPlacement } from './Dropdown/Menu'
import { MenuListItem } from './Dropdown/MenuItem'

// values are defined in locales files
enum DateDropdownValue {
  Today = 'dateDropdown.today',
  Yesterday = 'dateDropdown.yesterday',
  Last7Days = 'dateDropdown.last7Days',
  Last14Days = 'dateDropdown.last14Days',
  Last30Days = 'dateDropdown.last30Days',
  ThisWeek = 'dateDropdown.thisWeek',
  LastWeek = 'dateDropdown.lastWeek',
  ThisMonth = 'dateDropdown.thisMonth',
  LastMonth = 'dateDropdown.lastMonth',
  CustomRange = 'dateDropdown.customRange',
  Last60Days = 'dateDropdown.last60Days',
  Last90Days = 'dateDropdown.last90Days',
}

type DateRangeFactory = () => DateRange

interface DateItemData {
  label: DateDropdownValue
  getValue?: DateRangeFactory
}

interface DateRangeDropdownProps {
  dateTimeRange?: DateRange
  useQuickActionButton?: boolean
  onValueChange?: (dateRange: DateRange) => void
  useExtendedDates?: boolean
  isHeatmap?: boolean
  menuPlacement?: MenuPlacement
}

export const DateTimeRangeDropdown = ({
  dateTimeRange: externalDateRange,
  useQuickActionButton,
  onValueChange,
  useExtendedDates = false,
  isHeatmap = false,
  menuPlacement = MenuPlacement.BottomRight,
}: DateRangeDropdownProps) => {
  const dateItems: DateItemData[] = [
    ...(isHeatmap
      ? []
      : [
          {
            label: DateDropdownValue.Today,
            getValue: DateRange.today,
          },
          {
            label: DateDropdownValue.Yesterday,
            getValue: DateRange.yesterday,
          },
        ]),
    {
      label: DateDropdownValue.Last7Days,
      getValue: DateRange.last7Days,
    },
    {
      label: DateDropdownValue.Last14Days,
      getValue: DateRange.last14Days,
    },
    {
      label: DateDropdownValue.Last30Days,
      getValue: DateRange.last30Days,
    },
    ...(useExtendedDates
      ? [
          {
            label: DateDropdownValue.Last60Days,
            getValue: DateRange.last60Days,
          },
          {
            label: DateDropdownValue.Last90Days,
            getValue: DateRange.last90Days,
          },
        ]
      : []),
    {
      label: DateDropdownValue.CustomRange,
    },
  ]

  const dateRangeIndices = dateItems.reduce<Record<string, number>>(
    (accum, item, index) => {
      accum[item.label] = index
      return accum
    },
    {},
  )

  // Debug helper function
  const debugDateRangeComparison = (
    name: string,
    dateRange: DateRange,
    func: () => DateRange,
  ) => {
    const generatedRange = func()
    const isEqual = DateRange.areEqual(generatedRange, dateRange)
    return isEqual
  }

  const getIndexFromDateRange = (dateRange: DateRange) => {
    const isLast30Days = debugDateRangeComparison(
      'Last30Days',
      dateRange,
      DateRange.last30Days,
    )
    if (isLast30Days) {
      return dateRangeIndices[DateDropdownValue.Last30Days]
    }

    const isToday = debugDateRangeComparison(
      'Today',
      dateRange,
      DateRange.today,
    )
    if (isToday) {
      return dateRangeIndices[DateDropdownValue.Today]
    }

    const isYesterday = debugDateRangeComparison(
      'Yesterday',
      dateRange,
      DateRange.yesterday,
    )
    if (isYesterday) {
      return dateRangeIndices[DateDropdownValue.Yesterday]
    }

    const isLast7Days = debugDateRangeComparison(
      'Last7Days',
      dateRange,
      DateRange.last7Days,
    )
    if (isLast7Days) {
      return dateRangeIndices[DateDropdownValue.Last7Days]
    }

    const isLast14Days = debugDateRangeComparison(
      'Last14Days',
      dateRange,
      DateRange.last14Days,
    )
    if (isLast14Days) {
      return dateRangeIndices[DateDropdownValue.Last14Days]
    }

    const isLast60Days = debugDateRangeComparison(
      'Last60Days',
      dateRange,
      DateRange.last60Days,
    )
    if (isLast60Days) {
      return dateRangeIndices[DateDropdownValue.Last60Days]
    }

    const isLast90Days = debugDateRangeComparison(
      'Last90Days',
      dateRange,
      DateRange.last90Days,
    )
    if (isLast90Days) {
      return dateRangeIndices[DateDropdownValue.Last90Days]
    }

    return dateRangeIndices[DateDropdownValue.CustomRange]
  }

  const [selectedDateItemIndex, setSelectedDateItemIndex] = useState(0)
  const [dateRange, setDateRange] = useState<DateRange>(
    externalDateRange ?? DateRange.empty,
  )

  const [dateRangeModalOpen, setDateRangeModalOpen] = useState(false)

  const dateItemLabels = useMemo(() => {
    return dateItems.map((item, index) => {
      const content =
        index === selectedDateItemIndex ? (
          <MenuListItem
            primaryText={t(item?.label)}
            rightContent={<Icon as={Check} />}
          />
        ) : (
          t(item?.label)
        )
      return {
        value: t(item?.label?.toString() as TranslationKeys),
        content,
        buttonLabel: t(item?.label),
      }
    })
  }, [selectedDateItemIndex, dateItems])

  const handleDateItemSelected = (
    value: string,
    index: number,
    openModal = true,
  ) => {
    const isCustomRange =
      dateItems?.[index]?.label === DateDropdownValue.CustomRange

    if (isCustomRange) {
      if (openModal) {
        setDateRangeModalOpen(true)
        return
      }

      setSelectedDateItemIndex(index)

      if (externalDateRange) {
        setDateRange(externalDateRange)
        onValueChange?.(externalDateRange)
      }

      return
    }

    setSelectedDateItemIndex(index)
    if (!dateItems?.[index]) {
      return
    }

    const { getValue } = dateItems[index]
    if (!getValue) {
      return
    }

    const range = getValue()
    setDateRange(range)
    onValueChange?.(range)
  }

  //set selected date item index based on external date range
  useEffect(() => {
    if (!externalDateRange) return

    const dateRangeHasValue = hasValue(dateRange)
    const areDateRangesEqual = DateRange.areEqual(dateRange, externalDateRange)
    const targetIndex = getIndexFromDateRange(externalDateRange)
    const areIndicesEqual = selectedDateItemIndex === targetIndex
    const shouldHandleSelection =
      dateRangeHasValue && (!areDateRangesEqual || !areIndicesEqual)

    if (shouldHandleSelection) {
      handleDateItemSelected(
        DateDropdownValue.CustomRange.toString(),
        targetIndex ?? 0,
        false,
      )
      return
    }
  }, [externalDateRange])

  //on mount, set selected date item index if external date range is empty
  useEffect(() => {
    const needsInitialSet =
      (!hasValue(externalDateRange) ||
        DateRange.areEqual(externalDateRange, DateRange.empty)) &&
      DateRange.areEqual(dateRange, DateRange.empty)

    if (!needsInitialSet) {
      return
    }

    handleDateItemSelected(
      DateDropdownValue.Today.toString(),
      dateRangeIndices[DateDropdownValue.Today],
    )
  }, [])

  const handleDateRangeModalClose = () => {
    setDateRangeModalOpen(false)
  }

  const handleDateRangeModalSet = (dateTimeRange: DateRange) => {
    handleDateRangeModalClose()
    setDateRange(dateTimeRange)

    setSelectedDateItemIndex(dateRangeIndices[DateDropdownValue.CustomRange])
    onValueChange?.(dateTimeRange)
  }

  const handleDateRangeModalClear = () => {
    //signal modal data is cleared
    setDateRange({ ...DateRange.empty })
  }

  const tooltipLabel: string | React.ReactNode = useMemo(() => {
    const selectedItem = dateItemLabels[selectedDateItemIndex]

    const beforeText = hasValue(dateRange.after)
      ? t('dateTimeRange.after', { date: dateRange.after?.toLocaleString() })
      : undefined

    const afterText = hasValue(dateRange.before)
      ? t('dateTimeRange.before', { date: dateRange.before?.toLocaleString() })
      : undefined

    if (!selectedItem) {
      return (
        <Text color="fg.inverted" textAlign="center">
          {t('dateTimeRange.selectDateRange')}
        </Text>
      )
    }

    return (
      <>
        {useQuickActionButton && (
          <Text
            color="fg.inverted"
            textAlign="center"
            {...textStyles[textStyleKeys.Body2_Semibold]}
          >
            {selectedItem.buttonLabel}
          </Text>
        )}
        {beforeText && (
          <Text color="fg.inverted" textAlign="center">
            {beforeText}
          </Text>
        )}
        {afterText && (
          <Text color="fg.inverted" textAlign="center">
            {afterText}
          </Text>
        )}
      </>
    )
  }, [
    dateItemLabels,
    selectedDateItemIndex,
    dateRange.before,
    dateRange.after,
    useQuickActionButton,
  ])

  return (
    <Flex position="relative">
      {useQuickActionButton ? (
        <DropdownActionButton
          variant={QuickActionButtonVariants.Default_200}
          items={dateItemLabels}
          selectedIndex={selectedDateItemIndex}
          onSelect={handleDateItemSelected}
          icon={Calendar}
          tooltipText={tooltipLabel}
          menuOptions={{
            width: '18.75rem',
            maxHeight: '35vh',
            placement: menuPlacement,
          }}
        />
      ) : (
        <DropdownMangoButton
          variant={MangoButtonVariant.Default_200}
          items={dateItemLabels}
          selectedIndex={selectedDateItemIndex}
          onSelect={handleDateItemSelected}
          placeholder={t('dateTimeRange.buttonPlaceholder')}
          tooltipLabel={tooltipLabel}
          leftIcon={Calendar}
          menuOptions={{
            width: '18.75rem',
            maxHeight: '35vh',
            placement: menuPlacement,
          }}
        />
      )}
      <DateTimeRangeModal
        isOpen={dateRangeModalOpen}
        dateTimeRange={dateRange}
        onClear={handleDateRangeModalClear}
        onClose={handleDateRangeModalClose}
        onSet={handleDateRangeModalSet}
      />
    </Flex>
  )
}
